<template>
  <div class="chat-main">
    <div class="chat-container">
      <div class="chat-messages" ref="chatMessages" id="chat-box">
        <div v-for="(msg, index) in messages" :key="index">
          <div v-if="msg.role === 'user'" 
               :class="['message-bubble', 'user']">
            {{ msg.content }}
          </div>
          <div v-else-if="isProcessing(msg)" 
               :class="['message-bubble', 'thinking']">
            <div class="thinking-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          <div v-else 
               :class="['message-bubble', 'assistant']"
               :ref="'msg-' + index">
            <template v-if="smdParser && currentStreamIndex === index"></template>
            <template v-else>
              <span v-if="msg.status === 'streaming' && msg.displayedHtml" v-html="msg.displayedHtml"></span>
              <span v-else v-html="renderMarkdown(msg.content)"></span>
            </template>
          </div>
        </div>
      </div>
      <form class="chat-input" @submit.prevent="sendMessage">
        <div class="input-container">
          <input
            v-model="userInput"
            type="text"
            placeholder="Ask a question or start a conversation..."
            required
            class="message-input"
          />
          <button type="submit" class="send-button">
            <i class="las la-arrow-up"></i>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'

export default {
  name: 'ChatbotChat',
  props: {
    messages: {
      type: Array,
      default: () => []
    },
    selectedFile: {
      type: Object,
      default: null
    },
    sessionFiles: {
      type: Array,
      default: () => []
    },
    candidateId: {
      type: String,
      required: false
    },
    sessionId: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      userInput: '',
      websocket: null,
      // Streaming animation state
      chunkQueue: {},
      isAnimating: false,
      // Streaming markdown parser state
      smdParser: null,
      currentStreamIndex: null,
      chunks: '',
      smdModule: null,
      streamFlushTimer: null,
      websocketUrl: process.env.VUE_APP_WEBSOCKET_URL,
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true
    }
  },
  
  async mounted() {
    if (typeof window !== 'undefined' && !window.pdfjsLib) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
      };
      document.head.appendChild(script);
    }
    
    marked.setOptions({
      breaks: true,
      gfm: true,
      headerIds: false,
      mangle: false
    });
    
    this.$nextTick(() => {
      this.scrollToBottom();
    });

    // Ensure streaming dependencies are available at runtime
    await this.ensureStreamingDepsLoaded();

    // Connect to WebSocket if candidateId and sessionId are provided
    if (this.candidateId && this.sessionId) {
      this.connectWebSocket();
    }
  },
  beforeDestroy() {
    // Clean up WebSocket connection
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    if (this.streamFlushTimer) {
      clearTimeout(this.streamFlushTimer);
      this.streamFlushTimer = null;
    }
  },
  methods: {
    scrollToBottom() {
      if (this.$refs.chatMessages) {
        this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
      }
    },
    async ensureStreamingDepsLoaded() {
      const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

      try {
        if (typeof window !== 'undefined' && !window.DOMPurify) {
          await loadScript('https://cdn.jsdelivr.net/npm/dompurify@3.1.6/dist/purify.min.js');
        }
      } catch (e) {}

      try {
        if (!this.smdModule) {
          this.smdModule = await import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/streaming-markdown/smd.min.js');
        }
      } catch (e) {}
    },
    
    isProcessing(msg) {
      if (typeof msg === 'string') {
        return msg === 'Thinking...' || (typeof msg === 'string' && msg.includes('Processing...'));
      }
      
      if (typeof msg === 'object' && msg !== null) {
        return msg.content === 'Thinking...';
      }
      
      return false;
    },
    renderMarkdown(content) {
      // Ensure content is a string
      if (!content || typeof content !== 'string') {
        return content || '';
      }
      
      try {
        // Use marked library for proper markdown parsing
        return marked(content);
      } catch (error) {
        console.error('Error parsing markdown:', error);
        // Fallback to basic HTML escaping
        return this.escapeHtml(content);
      }
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    async sendMessage() {
      if (!this.userInput.trim()) return;
      
      const messageContent = this.userInput.trim();
      
      // Send message via WebSocket if connected
      if (this.candidateId && this.sessionId) {
        this.sendWebSocketMessage({
          content: messageContent,
          candidate_id: this.candidateId,
          session_id: this.sessionId
        });
      }
      
      this.$emit('send-message', messageContent);
      this.userInput = '';
      
      await this.$nextTick();
      this.scrollToBottom();
    },
    
    connectWebSocket() {
      try {
        const wsUrl = `${this.websocketUrl}/ws/chatbot/${this.candidateId}/${this.sessionId}/`;
        console.log('Connecting to WebSocket:', wsUrl);
        
        this.websocket = new WebSocket(wsUrl);
        
        this.websocket.onopen = () => {
          console.log('WebSocket connected successfully');
        };
        
        this.websocket.onmessage = (event) => {
          console.log('WebSocket message received');
          try {
            const data = JSON.parse(event.data);
            
              // Handle streaming chunks
              if (data.type === 'stream_chunk') {
                console.log('Streaming chunk received:', data.content);
                this.handleStreamChunk(data);
              } else {
                // Handle other message types (not stream chunks)
                this.$emit('websocket-message', data);
              }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        this.websocket.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
        
        this.websocket.onclose = () => {
          console.log('WebSocket disconnected');
        };
      } catch (error) {
        console.error('Error connecting to WebSocket:', error);
      }
    },
    
    disconnectWebSocket() {
      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }
    },
    
    sendWebSocketMessage(message) {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        message.message_type = 'chat_message';
        message.chat_type = 'financing';
        this.websocket.send(JSON.stringify(message));
      } else {
        console.warn('WebSocket is not connected. Cannot send message.');
      }
    },
    
    // --- HANDLE INCOMING STREAMING DATA ---
    handleStreamChunk(data) {
      let lastAssistantMessage = null;
      let messageIndex = -1;

      // find the currently streaming assistant message
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const candidate = this.messages[i];
        if (candidate && candidate.role === 'assistant' &&
            (candidate.status === 'streaming' || candidate.content === 'Thinking...')) {
          lastAssistantMessage = candidate;
          messageIndex = i;
          break;
        }
      }

      if (lastAssistantMessage) {
        if (lastAssistantMessage.content === 'Thinking...') {
          lastAssistantMessage.content = '';
          this.$set(lastAssistantMessage, 'displayedContent', '');
          this.$set(lastAssistantMessage, 'displayedHtml', '');
        }

        if (!lastAssistantMessage.displayedContent) {
          this.$set(lastAssistantMessage, 'displayedContent', '');
        }

        // Initialize streaming markdown renderer on first chunk for this message
        if (this.currentStreamIndex !== messageIndex) {
          this.startAssistantStream(messageIndex);
        }

        // Append raw chunk text (for markdown conversion fallback)
        const chunk = data.content || '';
        lastAssistantMessage.content += chunk;
        lastAssistantMessage.status = 'streaming';

        if (chunk && messageIndex >= 0) {
          // Simplified secure streaming pattern
          if (this.smdParser && this.currentStreamIndex === messageIndex && this.smdModule) {
            // Accumulate chunks for security check
            this.chunks = (this.chunks || '') + chunk;
            try {
              if (window && window.DOMPurify) {
                window.DOMPurify.sanitize(this.chunks);
                if (window.DOMPurify.removed && window.DOMPurify.removed.length) {
                  if (this.smdModule.parser_end) this.smdModule.parser_end(this.smdParser);
                  return;
                }
              }

              // Parse the current chunk
              if (this.smdModule.parser_write) this.smdModule.parser_write(this.smdParser, chunk);
              this.$nextTick(() => this.scrollToBottom());
            } catch (e) {
              this.enqueueChunk(messageIndex, chunk);
            }
          } else {
            // Fallback to existing typewriter animation
            this.enqueueChunk(messageIndex, chunk);
          }
        }
      }
    },

    // Initialize streaming markdown parser for a given message index
    startAssistantStream(messageIndex) {
      this.currentStreamIndex = messageIndex;
      this.buffer = '';
      this.chunks = '';

      let container = this.$refs['msg-' + messageIndex];
      if (Array.isArray(container)) container = container[0];
      if (!container) {
        this.$nextTick(() => {
          let retry = this.$refs['msg-' + messageIndex];
          if (Array.isArray(retry)) retry = retry[0];
          if (!retry) return;
          this.startAssistantStream(messageIndex);
        });
        return;
      }

      container.innerHTML = '';

      if (!this.smdModule || !this.smdModule.default_renderer || !this.smdModule.parser) {
        return;
      }

      const renderer = this.smdModule.default_renderer(container);
      this.smdParser = this.smdModule.parser(renderer);

      try {
        const existing = (this.messages[messageIndex] && this.messages[messageIndex].content) || '';
        if (existing && this.smdModule && this.smdModule.parser_write) {
          if (window && window.DOMPurify) {
            window.DOMPurify.sanitize(existing);
            if (window.DOMPurify.removed && window.DOMPurify.removed.length) {
              if (this.smdModule.parser_end) this.smdModule.parser_end(this.smdParser);
              return;
            }
          }
          this.smdModule.parser_write(this.smdParser, existing);
        }
      } catch (e) {
        
      }
    },

    finalizeAssistantStream() {
      if (this.smdParser && this.smdModule && this.smdModule.parser_end) {
        try { this.smdModule.parser_end(this.smdParser); } catch (_) {}
      }
      this.smdParser = null;
      this.currentStreamIndex = null;
      this.chunks = '';
    },

    // --- CHUNK QUEUE FOR SMOOTH RENDER ---
    enqueueChunk(messageIndex, chunk) {
      if (!this.chunkQueue) this.chunkQueue = {};
      if (!this.chunkQueue[messageIndex]) this.chunkQueue[messageIndex] = [];

      this.chunkQueue[messageIndex].push(chunk);
      this.processQueue(messageIndex);
    },

    async processQueue(messageIndex) {
      if (this.isAnimating) return; // prevent overlapping
      this.isAnimating = true;

      while (this.chunkQueue[messageIndex] && this.chunkQueue[messageIndex].length > 0) {
        const chunk = this.chunkQueue[messageIndex].shift();
        await this.animateChunk(messageIndex, chunk);
      }

      this.isAnimating = false;
    },

    // --- ANIMATE CHUNK CHARACTER-BY-CHARACTER ---
    async animateChunk(messageIndex, chunk) {
      const typingSpeed = 7; 
      for (let i = 0; i < chunk.length; i++) {
        const msg = this.messages[messageIndex];
        if (!msg) break;

        msg.displayedContent = (msg.displayedContent || '') + chunk[i];

        if (i % 10 === 0 || i === chunk.length - 1) {
          const formatted = this.renderMarkdown(msg.displayedContent);
          this.$set(msg, 'displayedHtml', formatted);
          this.$nextTick(() => this.scrollToBottom());
        }

        await new Promise(r => setTimeout(r, typingSpeed));
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.chat-main {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow: hidden;
  flex: 1;
  position: relative;
}

.chat-container {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

.chat-header {
  background: #ffffff;
  border-bottom: 1px solid #eaeaea;
  padding: 1rem 1.5rem;
  
  .header-title {
    @extend %title;
    font-size: 1.2rem;
    color: #333333;
  }
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  padding-bottom: 8rem;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  max-height: calc(100vh - 150px);
  min-height: 250px;
  margin-bottom: 2.5rem;
  position: relative;
  
  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    transition: background 0.2s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  }
  
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
  
  // Firefox scrollbar styling
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}

.message-bubble {
  padding: 0.75rem 1rem;
  font-family: $brand-font-regular;
  line-height: 1.4;
  word-wrap: break-word;
  
  &.user {
    margin-left: auto;
    background: $color-primary;
    color: $color-secondary;
    border-radius: 18px 18px 4px 18px;
    max-width: fit-content;
    min-width: 60px;
  }
  
  &.assistant {
    margin-right: auto;
    background: #ffffff;
    border: 1px solid #eaeaea;
    border-radius: 18px 18px 18px 4px;
    color: #333333;
    width: 100%;
    max-width: 100%;
    padding: 1rem 1.25rem;
    
    // Enhanced markdown styling
    :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
      margin: 1rem 0 0.5rem 0;
      color: #333333;
      font-family: $brand-font-medium;
      line-height: 1.3;
    }
    
    :deep(h1) { font-size: 1.5rem; }
    :deep(h2) { font-size: 1.3rem; }
    :deep(h3) { font-size: 1.1rem; }
    :deep(h4), :deep(h5), :deep(h6) { font-size: 1rem; }
    
    :deep(p) {
      margin: 0.5rem 0;
      line-height: 1.5;
    }
    
    :deep(strong) {
      font-weight: 600;
      color: #333333;
    }
    
    :deep(em) {
      font-style: italic;
      color: #555555;
    }
    
    :deep(code) {
      background: #f1f3f4;
      padding: 0.2rem 0.4rem;
      border-radius: 3px;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 0.9em;
      color: #d73a49;
    }
    
    :deep(pre) {
      background: #f6f8fa;
      padding: 1rem;
      border-radius: 6px;
      overflow-x: auto;
      margin: 0.75rem 0;
      border: 1px solid #e1e4e8;
      
      code {
        background: none;
        padding: 0;
        color: #24292e;
        font-size: 0.9em;
        line-height: 1.4;
      }
    }
    
    :deep(blockquote) {
      border-left: 4px solid $color-primary;
      margin: 0.75rem 0;
      padding: 0.5rem 0 0.5rem 1rem;
      background: #f8f9fa;
      border-radius: 0 4px 4px 0;
      color: #555555;
      font-style: italic;
    }
    
    :deep(ol), :deep(ul) {
      margin: 0.75rem 0;
      padding-left: 1.5rem;
      color: #333333;
    }
    
    :deep(li) {
      margin: 0.25rem 0;
      line-height: 1.4;
    }
    
    :deep(table) {
      border-collapse: collapse;
      width: 100%;
      margin: 0.75rem 0;
      font-size: 0.9em;
    }
    
    :deep(th), :deep(td) {
      border: 1px solid #e1e4e8;
      padding: 0.5rem;
      text-align: left;
    }
    
    :deep(th) {
      background: #f6f8fa;
      font-weight: 600;
    }
    
    :deep(hr) {
      border: none;
      border-top: 1px solid #e1e4e8;
      margin: 1rem 0;
    }
    
    :deep(a) {
      color: $color-primary;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    :deep(img) {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 0.5rem 0;
    }
    
    // Apply smooth transitions to content
    span {
      transition: opacity 0.15s ease;
    }
  }
  
  &.thinking {
    margin-right: auto;
    background: #ffffff;
    border: 1px solid #eaeaea;
    border-radius: 18px 18px 18px 4px;
    color: #333333;
  }
}

.thinking-dots {
  display: flex;
  gap: 0.25rem;
  
  span {
    width: 8px;
    height: 8px;
    background: $color-primary;
    border-radius: 50%;
    animation: thinking 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
    &:nth-child(3) { animation-delay: 0s; }
  }
}

@keyframes thinking {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0.4;
  }
  to {
    opacity: 1;
  }
}

// Typing cursor style
.typing-cursor {
  display: inline-block;
  color: $color-primary;
  font-weight: bold;
  margin-left: 2px;
  animation: blink 1s steps(2, start) infinite;
}

@keyframes blink {
  0%, 49% {
    opacity: 1;
  }
  50%, 100% {
    opacity: 0;
  }
}

.chat-input {
  padding: 0.5rem 1rem;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: transparent;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 10;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid #dee2e6;
  border-radius: 30px;
  padding: 0.5rem;
  padding-right: 0.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  &:focus-within {
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba(115, 243, 169, 0.1), 0 4px 20px rgba(0, 0, 0, 0.15);
  }
}

.message-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 12px;
  font-family: $brand-font-regular;
  font-size: 0.95rem;
  background: transparent;
  color: #495057;
  
  &:focus {
    outline: none;
    color: #495057;
    background: transparent;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
  
  &:disabled {
    background: transparent;
    color: #6c757d;
    cursor: not-allowed;
  }
}

.send-button {
  background: $color-primary;
  color: $color-background;
  border: none;
  padding: 0.25rem;
  border-radius: 50%;
  font-family: $brand-font-medium;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  
  &:hover:not(:disabled) {
    background: darken($color-primary, 10%);
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    background: #6c757d;
    color: #ffffff;
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
  
  i {
    font-size: 0.9rem;
    color: $color-background;
  }
}
</style> 